import { fetchRoomIdForAgentId } from '#~/api/agent/registry.js';
import type { Agent } from '@studio/models/agent';
import type { Room } from '@studio/models/room';
import { DEFAULT_BRIDGE_ONLINE_TIMEOUT_MS } from './auto-link-types.js';

// Adaptive polling: start fast (500ms) for the first few polls since the
// link often propagates within 1-2s, then back off to the standard interval
// for subsequent polls to avoid excessive API load.
export const BRIDGE_POLL_INITIAL_INTERVAL_MS = 500;
export const BRIDGE_POLL_INITIAL_COUNT = 3;
export const BRIDGE_POLL_INTERVAL_MS = 2_000;

export interface BridgeMonitorResult {
  readonly linked: boolean;
  readonly timedOut: boolean;
}

const fetchRoomIdWithAbort = async (
  bridgeId: Agent['id'],
  token: string,
  signal: AbortSignal
): Promise<{ readonly aborted: boolean; readonly roomId: Room['id'] | undefined }> => {
  if (signal.aborted) {
    return { aborted: true, roomId: undefined };
  }

  return new Promise((resolve, reject) => {
    const onAbort = (): void => {
      signal.removeEventListener('abort', onAbort);
      resolve({ aborted: true, roomId: undefined });
    };

    signal.addEventListener('abort', onAbort);

    fetchRoomIdForAgentId(bridgeId, token)
      .then((roomId) => {
        signal.removeEventListener('abort', onAbort);
        resolve({ aborted: false, roomId });
      })
      .catch((error) => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      });
  });
};

const waitForDelayOrAbort = async (
  delayMs: number,
  signal: AbortSignal
): Promise<'elapsed' | 'aborted'> => {
  if (signal.aborted) {
    return 'aborted';
  }

  return new Promise((resolve) => {
    const onAbort = (): void => {
      clearTimeout(timeoutId);
      signal.removeEventListener('abort', onAbort);
      resolve('aborted');
    };
    const timeoutId = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve('elapsed');
    }, delayMs);

    signal.addEventListener('abort', onAbort);
  });
};

/**
 * Wait for a bridge link to propagate after linking.
 * This verifies registry linkage only; it does not assert media/session readiness.
 *
 * @param bridgeId - The bridge agent ID
 * @param roomId - The room the bridge is linked to
 * @param token - Auth token
 * @param signal - AbortSignal for cancellation
 * @param timeoutMs - Maximum time to wait
 */
export const waitForBridgeLinked = async (
  bridgeId: Agent['id'],
  roomId: Room['id'],
  token: string,
  signal: AbortSignal,
  timeoutMs: number = DEFAULT_BRIDGE_ONLINE_TIMEOUT_MS
): Promise<BridgeMonitorResult> => {
  const startTime = Date.now();
  let pollCount = 0;

  while (Date.now() - startTime < timeoutMs) {
    if (signal.aborted) {
      return { linked: false, timedOut: false };
    }

    try {
      const lookup = await fetchRoomIdWithAbort(bridgeId, token, signal);

      if (lookup.aborted) {
        return { linked: false, timedOut: false };
      }

      if (lookup.roomId === roomId) {
        return { linked: true, timedOut: false };
      }
    } catch (error) {
      console.warn('[auto-link-bridge-monitor] Poll failed, retrying...', error);
    }

    pollCount++;
    const remainingMs = timeoutMs - (Date.now() - startTime);

    if (remainingMs <= 0) {
      break;
    }

    // Adaptive interval: poll fast initially (500ms x 3) then back off to 2s.
    // Link propagation typically completes within 1-2s so fast initial polls
    // shave ~1.5s off the common case without increasing API load on slow links.
    const intervalMs =
      pollCount <= BRIDGE_POLL_INITIAL_COUNT
        ? BRIDGE_POLL_INITIAL_INTERVAL_MS
        : BRIDGE_POLL_INTERVAL_MS;

    const waitResult = await waitForDelayOrAbort(Math.min(intervalMs, remainingMs), signal);

    if (waitResult === 'aborted') {
      return { linked: false, timedOut: false };
    }
  }

  return { linked: false, timedOut: true };
};

// Backward-compatible alias.
export const waitForBridgeReady = waitForBridgeLinked;

/**
 * Quick check if bridge is linked to expected room.
 * Used for fast verification without waiting.
 */
export const verifyBridgeLinked = async (
  bridgeId: Agent['id'],
  roomId: Room['id'],
  token: string
): Promise<boolean> => {
  try {
    const linkedRoomId = await fetchRoomIdForAgentId(bridgeId, token);
    return linkedRoomId === roomId;
  } catch {
    return false;
  }
};
